import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

interface ResearchActivity {
  id: string;
  type: 'monthly_log' | 'literature_note' | 'research_design' | 'research_figure' | 'term_definition' | 'research_output';
  title: string;
  author: string;
  created_at: string;
}

interface ResearchActivityWidgetProps {
  layoutMode?: 'normal' | 'compact';
}

export default function ResearchActivityWidget({ layoutMode = 'normal' }: ResearchActivityWidgetProps) {
  const [activities, setActivities] = useState<ResearchActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentActivities();
  }, []);

  const fetchRecentActivities = async () => {
    try {
      const allActivities: ResearchActivity[] = [];

      // 進捗ログ
      const { data: logs } = await supabase
        .from('monthly_logs')
        .select('id, title, author, created_at')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (logs) {
        allActivities.push(...logs.map(log => ({
          ...log,
          type: 'monthly_log' as const
        })));
      }

      // 参考文献
      const { data: literature } = await supabase
        .from('literature_notes')
        .select('id, title, author, created_at')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (literature) {
        allActivities.push(...literature.map(lit => ({
          ...lit,
          type: 'literature_note' as const
        })));
      }

      // 分析方法
      const { data: designs } = await supabase
        .from('research_designs')
        .select('id, title, author, created_at')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (designs) {
        allActivities.push(...designs.map(design => ({
          ...design,
          type: 'research_design' as const
        })));
      }

      // 図表
      const { data: figures } = await supabase
        .from('research_figures')
        .select('id, title, author, created_at')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (figures) {
        allActivities.push(...figures.map(fig => ({
          ...fig,
          type: 'research_figure' as const
        })));
      }

      // 用語集
      const { data: terms } = await supabase
        .from('term_definitions')
        .select('id, term as title, author, created_at')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (terms) {
        allActivities.push(...terms.map(term => ({
          ...term,
          type: 'term_definition' as const
        })));
      }

      // 提出物
      const { data: outputs } = await supabase
        .from('research_outputs')
        .select('id, title, author, created_at')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (outputs) {
        allActivities.push(...outputs.map(output => ({
          ...output,
          type: 'research_output' as const
        })));
      }

      // 日付順にソートして最新5件を取得
      allActivities.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setActivities(allActivities.slice(0, 5));
    } catch (error) {
      console.error('研究活動の取得に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type: ResearchActivity['type']) => {
    switch (type) {
      case 'monthly_log':
        return 'ri-calendar-check-line';
      case 'literature_note':
        return 'ri-book-open-line';
      case 'research_design':
        return 'ri-flask-line';
      case 'research_figure':
        return 'ri-bar-chart-box-line';
      case 'term_definition':
        return 'ri-book-mark-line';
      case 'research_output':
        return 'ri-file-text-line';
      default:
        return 'ri-file-line';
    }
  };

  const getTypeLabel = (type: ResearchActivity['type']) => {
    switch (type) {
      case 'monthly_log':
        return '進捗ログ';
      case 'literature_note':
        return '参考文献';
      case 'research_design':
        return '分析方法';
      case 'research_figure':
        return '図表';
      case 'term_definition':
        return '用語集';
      case 'research_output':
        return '提出物';
      default:
        return '投稿';
    }
  };

  const getTypeColor = (type: ResearchActivity['type']) => {
    switch (type) {
      case 'monthly_log':
        return 'from-teal-500 to-emerald-500';
      case 'literature_note':
        return 'from-blue-500 to-cyan-500';
      case 'research_design':
        return 'from-purple-500 to-pink-500';
      case 'research_figure':
        return 'from-orange-500 to-red-500';
      case 'term_definition':
        return 'from-green-500 to-teal-500';
      case 'research_output':
        return 'from-indigo-500 to-purple-500';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'たった今';
    if (diffMins < 60) return `${diffMins}分前`;
    if (diffHours < 24) return `${diffHours}時間前`;
    if (diffDays < 7) return `${diffDays}日前`;
    
    return date.toLocaleDateString('ja-JP', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg ${layoutMode === 'compact' ? 'p-4' : 'p-6'}`}>
      <h3 className={`font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2 ${layoutMode === 'compact' ? 'text-base' : 'text-lg'}`}>
        <i className="ri-flask-line text-teal-600"></i>
        研究活動
      </h3>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
        </div>
      ) : (
        <div className={`space-y-3 ${layoutMode === 'compact' ? 'text-sm' : ''}`}>
          {activities.map((activity) => (
            <div
              key={activity.id}
              className={`flex items-start gap-3 ${layoutMode === 'compact' ? 'p-2' : 'p-3'} bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200`}
            >
              <div className={`bg-gradient-to-br ${getTypeColor(activity.type)} rounded-lg flex items-center justify-center flex-shrink-0 ${layoutMode === 'compact' ? 'w-8 h-8 text-base' : 'w-10 h-10 text-lg'}`}>
                <i className={`${getTypeIcon(activity.type)} text-white`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 dark:text-white truncate">
                  {activity.title}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-gray-400 dark:text-gray-500 ${layoutMode === 'compact' ? 'text-xs' : 'text-xs'}`}>
                    {getTypeLabel(activity.type)}
                  </span>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <span className={`text-gray-500 dark:text-gray-400 ${layoutMode === 'compact' ? 'text-xs' : 'text-sm'}`}>
                    {formatDate(activity.created_at)}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {activities.length === 0 && (
            <div className="text-center py-8">
              <i className="ri-flask-line text-4xl text-gray-300 dark:text-gray-600 mb-2"></i>
              <p className="text-gray-500 dark:text-gray-400">研究活動がありません</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}